import React, { useState, useRef, useEffect } from 'react';

// Floating, draggable step checklist for a lab. Mounted at the app layout level
// (like BuzzAI) so it stays visible while the user works in the Pipeline view —
// letting them follow a lab hands-on instead of flipping back to the Labs page.

function formatStep(text) {
  return text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

export default function LabCompanion({ lab, onClose, onNavigate, currentPage }) {
  const [minimized, setMinimized] = useState(false);
  const [done, setDone] = useState({}); // step index -> checked
  const [pos, setPos] = useState(() => ({ x: Math.max(8, window.innerWidth - 380), y: 90 }));
  const drag = useRef(null);

  // Reset checklist + expand whenever a different lab is launched
  useEffect(() => { setDone({}); setMinimized(false); }, [lab?.id]);

  if (!lab) return null;

  const onMouseDown = (e) => {
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    const move = (ev) => {
      if (!drag.current) return;
      setPos({
        x: Math.max(8, Math.min(window.innerWidth - 120, ev.clientX - drag.current.dx)),
        y: Math.max(8, Math.min(window.innerHeight - 60, ev.clientY - drag.current.dy)),
      });
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const completed = Object.values(done).filter(Boolean).length;
  const total = lab.steps.length;

  return (
    <div className={`lab-companion${minimized ? ' minimized' : ''}`} style={{ left: pos.x, top: pos.y }}>
      <div className="lab-companion-hdr" onMouseDown={onMouseDown}>
        <span className="lab-companion-grip" aria-hidden>⠿</span>
        <span className="lab-companion-title">{lab.title}</span>
        <span className="lab-companion-progress">{completed}/{total}</span>
        <button className="lab-companion-btn" onClick={() => setMinimized(m => !m)} title={minimized ? 'Expand' : 'Minimize'}>
          {minimized ? '▢' : '—'}
        </button>
        <button className="lab-companion-btn" onClick={onClose} title="Close">✕</button>
      </div>

      {!minimized && (
        <div className="lab-companion-body">
          <div className="lab-companion-sub">Tick steps off as you go on the canvas:</div>
          <ol className="lab-companion-steps">
            {lab.steps.map((step, i) => (
              <li key={i} className={done[i] ? 'done' : ''}>
                <label>
                  <input
                    type="checkbox"
                    checked={!!done[i]}
                    onChange={() => setDone(d => ({ ...d, [i]: !d[i] }))}
                  />
                  <span dangerouslySetInnerHTML={{ __html: formatStep(step) }} />
                </label>
              </li>
            ))}
          </ol>
          {currentPage !== 'pipeline' && (
            <button className="lab-companion-goto" onClick={() => onNavigate('pipeline')}>
              Go to Pipeline →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
